import { computed, type Ref } from 'vue'
import { formatISO } from 'date-fns'
import { useHead } from '@unhead/vue'

import { ImageSrcSetFormat } from '@xcel-next/rendering-tools/types/constants'

import { useEpgAirTimeHelper } from './use-epg-air-time-helper'
import { useImgSrcSet } from './use-img-src-set'
import { usePageStore } from '../stores/page'
import { hashString } from '../utils/strings'
import { deleteEmptyProperties } from '../utils/objects'

import type { EpgWidgetParagraph } from '../utils/paragraph-schema/paragraphs'


type EpgWidget = EpgWidgetParagraph['epgWidget']
type EpgResultSet = NonNullable<EpgWidget['initialBroadcasts']>[number]
// The structured-data JSON-LD builder below needs `episode`/`season`, which aren't (yet) part of the
// composer-schema broadcast type but are present on the actual API response.
type EpgBroadcastItem = EpgResultSet['broadcasts'][number] & { episode?: number, season?: number | string }
type EpgBroadcastForItemList = EpgBroadcastItem & { channelName?: string }

type InitialFilters = NonNullable<EpgWidget['initialFilters']>
type Config = NonNullable<EpgWidget['config']>

type SchemaOrgTvSeries = {
  '@type': 'TVSeries',
  name?: string
}

type SchemaOrgTvSeason = {
  '@type': 'TVSeason',
  seasonNumber: number | string,
  partOfSeries?: SchemaOrgTvSeries
}

type SchemaOrgWorkFeatured = {
  '@type': 'Movie' | 'TVEpisode',
  name?: string,
  episodeNumber?: number,
  partOfSeason?: SchemaOrgTvSeason,
  partOfSeries?: SchemaOrgTvSeries,
  image?: string | null,
  isFamilyFriendly?: boolean,
  contentRating?: string
}

/**
 * Builds & registers the schema.org `ItemList`/`BroadcastEvent` JSON-LD structured data for a list of EPG broadcasts.
 * Composition API counterpart of the `epgStructuredDataBroadcastItemList` mixin
 * (`../apps/tvmovie/theme/mixins/epg-structured-data-broadcast-item-list.js`), used exclusively by
 * `ParagraphTypeEpgSearch.vue`.
 */
export function useEpgStructuredData (params: {
  initialFilters: Ref<InitialFilters>,
  config: Ref<Config>,
  broadcasts: Ref<EpgResultSet[]>
}) {
  const { initialFilters, config, broadcasts } = params

  const { forceDateIntoTimeZone } = useEpgAirTimeHelper()
  const { createImgSrc } = useImgSrcSet()
  const pageStore = usePageStore()

  const vmid = computed(() => 'json-ld-epg-broadcast-item-list-' + hashString(JSON.stringify(initialFilters.value)))

  // create a neat flat-mapped array of broadcasts with the channel name included, makes iteration easier in `buildJsonLd()`
  const broadcastsForItemList = computed<EpgBroadcastForItemList[]>(() => {
    const rows = Array.isArray(broadcasts.value) ? broadcasts.value : []
    return rows.flatMap((row) => (row?.broadcasts || []).map((broadcast) => ({ ...broadcast, channelName: row?.channel?.name })))
  })

  function getBroadcastImageUrl (image: EpgBroadcastItem['image']) {
    return createImgSrc({
      image: image ?? {},
      brand: pageStore.brandFromStore,
      type: 'webp',
      width: 1200, // google: "Images must have a high resolution..." - https://developers.google.com/search/docs/appearance/structured-data/movie#movie
      srcSetDefinition: {
        format: ImageSrcSetFormat.Rectangle
      }
    })
  }

  function buildJsonLd () {
    return deleteEmptyProperties({
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      name: config.value?.headline,
      itemListOrder: 'ItemListOrderAscending',
      itemListElement: broadcastsForItemList.value.map((broadcast, i) => {
        if (!broadcast) return undefined

        let workFeatured: SchemaOrgWorkFeatured

        if (broadcast.categoryName === 'Film' || (broadcast.genreName?.includes('film') && !['Kurzfilmmagazin', 'Kurzfilm-Kompilation'].includes(broadcast.genreName))) {
          workFeatured = {
            '@type': 'Movie',
            name: broadcast.title
          }
        } else {
          // TODO live sports matches? not sure how we'd identify them...

          workFeatured = {
            '@type': 'TVEpisode',
            name: broadcast.subTitle || broadcast.title,
            episodeNumber: broadcast.episode // will be deleted if undefined
          }

          // only add partOfSeries/TVSeries if we have a subTitle for TVEpisode that differs from title, otherwise it's not of much use
          const partOfSeries: SchemaOrgTvSeries | undefined = broadcast.subTitle && broadcast.subTitle !== broadcast.title
            ? {
              '@type': 'TVSeries',
              name: broadcast.title
            }
            : undefined

          // partOfSeason/TVSeason we can always add if we have a season number ("0" is a valid season number)
          if (broadcast.season !== undefined && broadcast.season !== null && (broadcast.season as unknown) !== '') {
            workFeatured.partOfSeason = {
              '@type': 'TVSeason',
              seasonNumber: broadcast.season
            }

            // nest partOfSeries/TVSeries inside partOfSeason/TVSeason
            if (partOfSeries) {
              workFeatured.partOfSeason.partOfSeries = partOfSeries
            }
          } else if (partOfSeries) {
            // without season number, just add partOfSeries/TVSeries directly to workFeatured/TVEpisode
            workFeatured.partOfSeries = partOfSeries
          }
        }

        const startDate = formatISO(forceDateIntoTimeZone(broadcast.airTime!))
        const endDate = formatISO(forceDateIntoTimeZone(broadcast.airTimeEnd!))
        const isHd = broadcast.flags?.isHD || false
        const isYouthProtected = broadcast.flags?.isYouthProtected || false
        const isFamilyFriendly = broadcast.categoryName === 'Kinder' || broadcast.genreName?.startsWith('Kinder') ? true : undefined

        workFeatured.image = getBroadcastImageUrl(broadcast.image)
        workFeatured.isFamilyFriendly = isFamilyFriendly ?? (isYouthProtected ? false : undefined)
        workFeatured.contentRating = broadcast.ageRating ? `FSK ${broadcast.ageRating.toString()}` : undefined

        return {
          '@type': 'ListItem',
          position: i + 1,
          item: {
            '@type': 'BroadcastEvent',
            name: broadcast.title,
            startDate,
            endDate,
            isLiveBroadcast: broadcast.flags?.isLive || false,
            inLanguage: 'de',
            videoFormat: isHd ? 'HD' : 'SD',
            publishedOn: {
              '@type': 'BroadcastService',
              name: broadcast.channelName,
              broadcastDisplayName: broadcast.channelName + (isHd ? ' HD' : '')
            },
            workFeatured
          }
        }
      }).filter(Boolean)
    })
  }

  const jsonLdData = computed(() => {
    if (!broadcastsForItemList.value.length) return undefined
    return buildJsonLd()
  })

  useHead(computed(() => {
    if (!jsonLdData.value) return {}
    return {
      script: [{
        'data-vmid': vmid.value,
        type: 'application/ld+json',
        innerHTML: JSON.stringify(jsonLdData.value)
      }]
    }
  }))
}
